import { z } from 'zod';
import { EINHEIT_ARTEN, PERSONEN_TYPEN } from '../constants.js';

/** Stammdaten (SPEC Kap. 3). Dieselben Regeln gelten im Client und im Import. */

export const plzSchema = z.string().regex(/^\d{5}$/, 'PLZ muss aus genau 5 Ziffern bestehen');

export const objektNrSchema = z
  .string()
  .min(1, 'Objektnummer ist ein Pflichtfeld')
  .max(60, 'Objektnummer darf höchstens 60 Zeichen lang sein')
  .regex(/^[A-Za-z0-9._\-/]+$/, 'Objektnummer darf nur Buchstaben, Ziffern und . _ - / enthalten');

export const ausstattungSchema = z.object({
  bezeichnung: z.string().min(1, 'Bezeichnung ist ein Pflichtfeld').max(200),
  anzahl: z.number().min(0).max(9999).optional(),
  zustand: z.string().max(60).optional(),
  hersteller: z.string().max(120).optional(),
  seriennummer: z.string().max(120).optional(),
});
export type Ausstattung = z.infer<typeof ausstattungSchema>;

export const raumSchema = z.object({
  id: z.string().optional(),
  objektId: z.string().optional(),
  /** Räume hängen an der Einheit; ohne Einheit an der Standardeinheit des Objekts. */
  einheitId: z.string().nullable().optional(),
  name: z.string().min(1, 'Raumname ist ein Pflichtfeld').max(120),
  flaeche: z.number().min(0).max(10_000).nullable().optional(),
  reihenfolge: z.number().int().min(0).max(999).nullable().optional(),
  ausstattung: z.array(ausstattungSchema).optional(),
  boden: z.string().max(120).optional(),
  wand: z.string().max(120).optional(),
  decke: z.string().max(120).optional(),
});
export type Raum = z.infer<typeof raumSchema>;

export const schluesselStammSchema = z.object({
  bezeichnung: z.string().min(1).max(120),
  anzahl: z.number().int().min(0).max(999),
  nummer: z.string().max(120).optional(),
});
export type SchluesselStamm = z.infer<typeof schluesselStammSchema>;

export const objektSchema = z.object({
  id: z.string().optional(),
  objektNr: objektNrSchema,
  bezeichnung: z.string().min(1, 'Bezeichnung ist ein Pflichtfeld').max(200),
  strasse: z.string().max(200).optional(),
  plz: plzSchema.optional().or(z.literal('')),
  ort: z.string().max(120).optional(),
  lage: z.string().max(120).optional(),
  wohnflaeche: z.number().min(0).max(100_000).nullable().optional(),
  zimmeranzahl: z.number().min(0).max(999).nullable().optional(),
  eigentuemer: z.string().max(200).optional(),
  notiz: z.string().max(4000).optional(),
  aktiv: z.boolean().optional(),
  /** Aus dem Import übernommene Schlüsselstammdaten. */
  schluessel: z.array(schluesselStammSchema).optional(),
});
export type Objekt = z.infer<typeof objektSchema>;

/**
 * Einheit — die übergebene Fläche innerhalb eines Objekts (SPEC Kap. 3).
 *
 * Ein Objekt ist das Gebäude oder die Liegenschaft, eine Einheit die Wohnung
 * oder Gewerbefläche darin. Räume hängen an der Einheit, ein Protokoll gilt
 * für genau eine Einheit. Objekte ohne echte Aufteilung (Einfamilienhaus)
 * bekommen automatisch eine Standardeinheit, damit dieselbe Kette überall gilt.
 */
export const einheitSchema = z.object({
  id: z.string().optional(),
  objektId: z.string().optional(),
  bezeichnung: z.string().min(1, 'Bezeichnung ist ein Pflichtfeld').max(200),
  /** Interne Nummer der Einheit, z. B. „WE 03". Muss je Objekt eindeutig sein. */
  einheitNr: z.string().max(60).optional(),
  art: z.enum(EINHEIT_ARTEN).optional(),
  etage: z.string().max(60).optional(),
  lage: z.string().max(120).optional(),
  wohnflaeche: z.number().min(0).max(100_000).nullable().optional(),
  zimmeranzahl: z.number().min(0).max(999).nullable().optional(),
  reihenfolge: z.number().int().min(0).max(999).nullable().optional(),
  notiz: z.string().max(4000).optional(),
  aktiv: z.boolean().optional(),
  /** Schlüssel der Einheit. Fehlen sie, gelten die Schlüssel des Objekts. */
  schluessel: z.array(schluesselStammSchema).optional(),
});
export type Einheit = z.infer<typeof einheitSchema>;

/**
 * ID der automatisch erzeugten Standardeinheit eines Objekts.
 *
 * Sie wird aus der Objekt-ID abgeleitet und ist damit auf jedem Gerät
 * dieselbe. Ohne diese Ableitung würde jedes Gerät, das ein Altobjekt zum
 * ersten Mal sieht, eine eigene Standardeinheit anlegen — bei drei Tablets
 * dreimal dieselbe Wohnung.
 */
export const standardEinheitId = (objektId: string): string => `${objektId}--einheit`;

export const istStandardEinheit = (einheitId: string): boolean => einheitId.endsWith('--einheit');

/** Anzeigename einer Einheit: „WE 03 · 2. OG rechts". */
export const einheitBezeichnung = (einheit: {
  bezeichnung?: string | null;
  einheitNr?: string | null;
  lage?: string | null;
}): string => {
  const kopf = [einheit.einheitNr, einheit.bezeichnung].filter(Boolean).join(' · ');
  return kopf || einheit.lage || 'Einheit';
};

export const personSchema = z.object({
  id: z.string().optional(),
  typ: z.enum(PERSONEN_TYPEN),
  vorname: z.string().max(120).optional(),
  nachname: z.string().min(1, 'Nachname ist ein Pflichtfeld').max(120),
  email: z.email('Keine gültige E-Mail-Adresse').optional().or(z.literal('')),
  telefon: z.string().max(60).optional(),
  firma: z.string().max(200).optional(),
});
export type Person = z.infer<typeof personSchema>;

/**
 * Anzeigename einer Person. Nimmt `null` ebenso wie `undefined` entgegen —
 * die lokale Datenbank speichert leere Felder als `null`, die Zod-Schemas
 * lassen sie weg.
 */
export const personName = (person: { vorname?: string | null; nachname?: string | null }): string =>
  [person.vorname, person.nachname].filter(Boolean).join(' ').trim();

/** Vollständige Anschrift eines Objekts, einzeilig. */
export const objektAnschrift = (objekt: {
  strasse?: string | null;
  plz?: string | null;
  ort?: string | null;
  lage?: string | null;
}): string => {
  const ortsteil = [objekt.plz, objekt.ort].filter(Boolean).join(' ').trim();
  return [objekt.strasse, ortsteil, objekt.lage]
    .filter((teil): teil is string => typeof teil === 'string' && teil.length > 0)
    .join(', ');
};
